import * as React from "react"
import { createPortal } from "react-dom"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { toast, useSonner, type ToastT } from "sonner"

import { buttonVariants } from "@/components/ui/button"
import { LiveRegion } from "@/components/ui/live-region"
import { useAppNavRail } from "@/lib/hooks/useAppNavRail"

/**
 * Story 24.2b — the shared Toast (DESIGN.md › Components › Toast). Roles only.
 *
 * sonner stays underneath as the store: every `toast.*` call site is
 * unchanged, but sonner's own `<Toaster>` is never mounted. This region reads
 * the store through `useSonner()` and draws one toast at a time.
 *
 * - One toast: when a new id arrives, every older id is `toast.dismiss`ed.
 *   A same-id update replaces the text in place and keeps its timer.
 * - Timing: an action toast and a loading toast have no timer. Any other toast closes after 6s,
 *   paused while hovered, holding focus or while the tab is hidden (it
 *   resumes with the time left).
 *   A per-call finite `duration` is ignored; `duration: Infinity` stays open.
 * - No Escape handler: nothing here reacts to Esc.
 * - Announced once, through the region's pre-mounted polite live region
 *   (title, plus ". " and the description). The card itself is not live.
 * - Placement: bottom-centre below 1024px; bottom-left clear of the app nav
 *   rail at 1024px and up (16px past its live width, Story 24.6), where the card's height (+32px) is written to
 *   `--toast-inset` on `<html>` so scroll containers that opt in with
 *   `data-scroll-container` keep the focused row clear of it.
 * - Story 24.7b — below 1024px the region sits `--bottom-chrome` (the live
 *   height of the tab bar or the thread composer, `src/lib/ui/bottomChrome.ts`)
 *   + `--kb-inset` (the iOS keyboard, 0 elsewhere) + 8px up, and the card
 *   writes its height (+16px) to
 *   `--toast-slot` on `<html>`. The in-flow bottom slot (`BottomSlot`) takes
 *   that height, so the toast occupies reserved space and covers no content.
 * - Stacking: the region is `z-40`, under scrim, dialogs and menus (`z-50`).
 * - Portalled to `document.body` (Story 24.3), so it stays outside `#root`,
 *   which an open Sheet or Dialog makes `inert`.
 * - Story 24.22 — `ToastAnchor`: a page may mount `<ToastAnchor />` right
 *   after its body. While one is mounted, the region is portalled into the
 *   latest anchor instead of `<body>`, so one Tab from the page's last
 *   control reaches the toast (an Undo). It stays `position: fixed`, so
 *   nothing moves on screen. Trade-off: inside `#root` the toast is inert
 *   while a Dialog or Sheet is open. With no anchor mounted nothing changes.
 * - When a toast closes while focus is inside it, focus moves to the page's
 *   `h1` (`[data-slot="page-header"] h1`) instead of dropping to `<body>`.
 */

const TOAST_DURATION_MS = 6000
const TOAST_INSET_GAP_PX = 32
const DESKTOP_QUERY = "(min-width: 1024px)"
const TOAST_INSET_PROPERTY = "--toast-inset"
const TOAST_SLOT_GAP_PX = 16
const TOAST_SLOT_PROPERTY = "--toast-slot"

/*
 * Story 24.22 — the anchor store. The latest mounted anchor wins; with none,
 * the region goes to `document.body`.
 */
const anchors: HTMLElement[] = []
const anchorListeners = new Set<() => void>()

function subscribeAnchors(listener: () => void) {
  anchorListeners.add(listener)
  return () => {
    anchorListeners.delete(listener)
  }
}

function currentAnchor(): HTMLElement | null {
  return anchors.length > 0 ? anchors[anchors.length - 1] : null
}

function setAnchorRegistered(node: HTMLElement, registered: boolean) {
  const index = anchors.indexOf(node)
  if (registered && index === -1) anchors.push(node)
  else if (!registered && index !== -1) anchors.splice(index, 1)
  else return
  anchorListeners.forEach((listener) => listener())
}

/**
 * Where the toast region lives on this page (Story 24.22). Mount it right
 * after the page's body, before any toast, so the region (and its live
 * region) is already in place when a toast arrives.
 */
function ToastAnchor() {
  const ref = React.useRef<HTMLDivElement>(null)
  React.useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    setAnchorRegistered(node, true)
    return () => setAnchorRegistered(node, false)
  }, [])
  return <div ref={ref} data-slot="toast-anchor" />
}

/** The page's h1 takes focus when a toast holding focus goes away. */
function focusPageHeading() {
  const heading = document.querySelector<HTMLElement>('[data-slot="page-header"] h1')
  heading?.focus({ preventScroll: true })
}

type ToastAction = { label: React.ReactNode; onClick: (event: React.MouseEvent<HTMLButtonElement>) => void }

function isActionObject(action: ToastT["action"]): action is ToastAction {
  return (
    typeof action === "object" &&
    action !== null &&
    !React.isValidElement(action) &&
    "label" in action &&
    "onClick" in action
  )
}

function resolveNode(value: ToastT["title"]): React.ReactNode {
  return typeof value === "function" ? value() : value
}

function plainText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node).trim()
  return ""
}

function announcementFor(t: ToastT): string {
  const title = plainText(resolveNode(t.title))
  const description = plainText(resolveNode(t.description))
  if (title && description) return `${title}. ${description}`
  return title || description
}

const STATUS_ICONS: Partial<Record<NonNullable<ToastT["type"]>, React.ReactNode>> = {
  success: <CheckCircleIcon aria-hidden="true" className="size-5 text-success" />,
  error: <ExclamationCircleIcon aria-hidden="true" className="size-5 text-danger" />,
  warning: <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-warning" />,
  info: <InformationCircleIcon aria-hidden="true" className="size-5 text-ink-muted" />,
}

function useDesktop(): boolean {
  const [desktop, setDesktop] = React.useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(DESKTOP_QUERY).matches
      : false
  )
  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") return
    const mql = window.matchMedia(DESKTOP_QUERY)
    setDesktop(mql.matches)
    const onChange = (event: MediaQueryListEvent) => setDesktop(event.matches)
    mql.addEventListener?.("change", onChange)
    return () => mql.removeEventListener?.("change", onChange)
  }, [])
  return desktop
}

/** True while the tab is in the background, so a toast never expires unseen. */
function useDocumentHidden(): boolean {
  const [hidden, setHidden] = React.useState(() => typeof document !== "undefined" && document.hidden)
  React.useEffect(() => {
    const onChange = () => setHidden(document.hidden)
    document.addEventListener("visibilitychange", onChange)
    return () => document.removeEventListener("visibilitychange", onChange)
  }, [])
  return hidden
}

function ToastCard({ toast: t, desktop }: { toast: ToastT; desktop: boolean }) {
  const cardRef = React.useRef<HTMLDivElement>(null)
  const remaining = React.useRef(TOAST_DURATION_MS)
  const [hovered, setHovered] = React.useState(false)
  const [focusWithin, setFocusWithin] = React.useState(false)
  const documentHidden = useDocumentHidden()

  const action = t.action
  // A loading toast (toast.loading / toast.promise) stays until it resolves.
  const persistent = Boolean(action) || t.type === "loading" || t.duration === Infinity
  const paused = hovered || focusWithin || documentHidden
  const id = t.id

  // Latest toast for the close callbacks, without restarting the timer on a
  // same-id update.
  const latest = React.useRef(t)
  latest.current = t

  React.useEffect(() => {
    if (persistent || paused) return
    const started = Date.now()
    const timer = window.setTimeout(() => {
      latest.current.onAutoClose?.(latest.current)
      toast.dismiss(id)
    }, Math.max(0, remaining.current))
    return () => {
      window.clearTimeout(timer)
      remaining.current -= Date.now() - started
    }
  }, [persistent, paused, id])

  // Desktop inset: the card floats over the content, so scroll containers
  // pad their end by its height while it is shown.
  React.useLayoutEffect(() => {
    const el = cardRef.current
    if (!desktop || !el) return
    const root = document.documentElement
    const write = () => root.style.setProperty(TOAST_INSET_PROPERTY, `${el.offsetHeight + TOAST_INSET_GAP_PX}px`)
    write()
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(write) : null
    observer?.observe(el)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(TOAST_INSET_PROPERTY)
    }
  }, [desktop])

  // Story 24.7b — phone reservation: the mirror of the desktop inset. The
  // bottom slot below the content grows by the card's height while it shows.
  React.useLayoutEffect(() => {
    const el = cardRef.current
    if (desktop || !el) return
    const root = document.documentElement
    const write = () => root.style.setProperty(TOAST_SLOT_PROPERTY, `${el.offsetHeight + TOAST_SLOT_GAP_PX}px`)
    write()
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(write) : null
    observer?.observe(el)
    return () => {
      observer?.disconnect()
      root.style.removeProperty(TOAST_SLOT_PROPERTY)
    }
  }, [desktop])

  // Closing with focus inside: never drop focus to <body>. This cleanup runs
  // while the card is still in the DOM, so `contains` sees the focused node.
  React.useLayoutEffect(() => {
    const el = cardRef.current
    return () => {
      if (el && el.contains(document.activeElement)) focusPageHeading()
    }
  }, [])

  const icon = t.type ? STATUS_ICONS[t.type] : undefined
  const title = resolveNode(t.title)
  const description = resolveNode(t.description)

  return (
    <div
      ref={cardRef}
      data-slot="toast"
      data-type={t.type ?? "default"}
      // A floating layer: ghost buttons inside hover with `hover-on-raised`.
      data-floating=""
      className="pointer-events-auto flex items-start gap-3 rounded-lg border border-line-floating bg-surface-raised p-2 pl-4 text-ink shadow-float"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusWithin(false)
      }}
    >
      {icon && <span className="flex min-h-11 shrink-0 items-center">{icon}</span>}
      <div className="flex min-h-11 min-w-0 flex-1 flex-col justify-center py-1">
        {title != null && title !== "" && <p className="type-body-sm break-words">{title}</p>}
        {description != null && description !== "" && (
          <p className="type-caption break-words text-ink-muted">{description}</p>
        )}
      </div>
      {isActionObject(action) ? (
        <button
          type="button"
          data-slot="toast-action"
          className={buttonVariants({ variant: "ghost" })}
          onClick={(event) => {
            action.onClick(event)
            if (!event.defaultPrevented) toast.dismiss(id)
          }}
        >
          {action.label}
        </button>
      ) : React.isValidElement(action) ? (
        action
      ) : null}
      <button
        type="button"
        data-slot="toast-close"
        aria-label="Dismiss notification"
        className={buttonVariants({ variant: "ghost", size: "icon" })}
        onClick={() => {
          latest.current.onDismiss?.(latest.current)
          toast.dismiss(id)
        }}
      >
        <XMarkIcon aria-hidden="true" />
      </button>
    </div>
  )
}

/** The app's one toast region. Mount once, at start-up (`App.tsx`). */
function Toaster() {
  const { toasts } = useSonner()
  const desktop = useDesktop()
  // Story 24.6 — clear of the app-nav rail's LIVE width (0 / 64 / 240).
  const railWidth = useAppNavRail().width
  const current = toasts[0] as ToastT | undefined
  const anchor = React.useSyncExternalStore(subscribeAnchors, currentAnchor, () => null)

  // One toast at a time: every older id is dismissed.
  React.useEffect(() => {
    toasts.slice(1).forEach((t) => toast.dismiss(t.id))
  }, [toasts])

  // Each toast's text is written to the live region once (again only if a
  // same-id update changes the text). When the toast goes, the region is
  // emptied so nobody browsing the landmark finds a stale notice.
  const text = current ? announcementFor(current) : ""
  const currentId = current?.id
  const [live, setLive] = React.useState({ message: "", nonce: 0 })
  React.useEffect(() => {
    if (currentId === undefined) {
      setLive((prev) => (prev.message ? { message: "", nonce: prev.nonce + 1 } : prev))
      return
    }
    if (!text) return
    setLive((prev) => ({ message: text, nonce: prev.nonce + 1 }))
  }, [currentId, text])

  return createPortal(
    <section
      aria-label="Notifications"
      data-slot="toast-region"
      className="pointer-events-none fixed inset-x-4 bottom-[calc(var(--bottom-chrome,0px)+var(--kb-inset,0px)+8px)] z-40 mx-auto max-w-md lg:inset-x-auto lg:bottom-4 lg:left-(--toast-left) lg:w-full lg:max-w-sm"
      style={{ "--toast-left": `${railWidth + 16}px` } as React.CSSProperties}
    >
      <LiveRegion data-slot="toast-live" politeness="polite" {...live} />
      {current && <ToastCard key={current.id} toast={current} desktop={desktop} />}
    </section>,
    anchor ?? document.body
  )
}

export { Toaster, ToastAnchor }
